<script setup>
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const assistantId = computed(() => Number(route.params.assistantId));

// All account-level MCP servers (the catalog the admin can attach
// from). Already loaded by the account-level MCP page; we re-fetch on
// mount so this page works as a deep link too.
const allServersGetter = useMapGetter('captainMcpServers/getRecords');
const allServersFlagsGetter = useMapGetter('captainMcpServers/getUIFlags');

// The assistant↔server links live in their own store (custom shape;
// not the createStore factory) so the records can be keyed cleanly.
const linksByServerGetter = useMapGetter(
  'captainAssistantMcpServers/byServerId'
);
const linksFlagsGetter = useMapGetter('captainAssistantMcpServers/uiFlags');

// Defensive wrappers — getters can return undefined for a tick while
// the store module is still being registered or the first dispatch is
// in flight. Without these the empty-state / body branch in PageLayout
// silently skips both slots and the page reads as blank.
const allServers = computed(() =>
  Array.isArray(allServersGetter.value) ? allServersGetter.value : []
);
const linksByServer = computed(() => linksByServerGetter.value || {});
const isFetching = computed(
  () =>
    Boolean(allServersFlagsGetter.value?.fetchingList) ||
    Boolean(linksFlagsGetter.value?.fetching)
);

const fetchAll = async () => {
  if (!assistantId.value) return;
  // Run both in parallel; never let a rejection break the page render.
  // Errors get logged so they're visible in the dev console.
  try {
    await Promise.all([
      store.dispatch('captainMcpServers/get'),
      store.dispatch('captainAssistantMcpServers/fetch', assistantId.value),
    ]);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[Captain MCP] failed to load page data', error);
  }
};

watch(assistantId, fetchAll);
onMounted(fetchAll);

const isAttached = serverId => Boolean(linksByServer.value[serverId]);

const enabledToolsFor = serverId => {
  const link = linksByServer.value[serverId];
  // null = "all tools enabled" — UI shows everything checked.
  // array  = explicit whitelist.
  return link?.enabled_tools ?? null;
};

const isToolEnabled = (server, toolName) => {
  const enabledTools = enabledToolsFor(server.id);
  if (enabledTools === null) return true;
  return enabledTools.includes(toolName);
};

const handleAttachToggle = async server => {
  try {
    if (isAttached(server.id)) {
      await store.dispatch('captainAssistantMcpServers/detach', {
        assistantId: assistantId.value,
        serverId: server.id,
      });
      useAlert(t('CAPTAIN.ASSISTANT_MCP.DETACHED'));
    } else {
      await store.dispatch('captainAssistantMcpServers/attach', {
        assistantId: assistantId.value,
        serverId: server.id,
        enabledTools: null,
      });
      useAlert(t('CAPTAIN.ASSISTANT_MCP.ATTACHED'));
    }
  } catch (error) {
    useAlert(error?.message || t('CAPTAIN.ASSISTANT_MCP.SAVE_ERROR'));
  }
};

// Compute the new whitelist after toggling one tool, then PUT.
const handleToolToggle = async (server, toolName, isCurrentlyEnabled) => {
  const allToolNames = (server.discovered_tools || []).map(tt => tt.name);
  const currentEnabled = enabledToolsFor(server.id);

  let nextEnabled;
  if (isCurrentlyEnabled) {
    // Removing a tool. If we had null (all), materialize the full list
    // first so we can subtract.
    const baseline =
      currentEnabled === null ? [...allToolNames] : [...currentEnabled];
    nextEnabled = baseline.filter(n => n !== toolName);
  } else {
    const baseline = currentEnabled === null ? [] : [...currentEnabled];
    nextEnabled = [...new Set([...baseline, toolName])];
  }

  // If the user just re-enabled every tool, collapse back to null
  // ("all enabled") so future server-side discovery additions are
  // automatically picked up.
  if (nextEnabled.length === allToolNames.length) {
    nextEnabled = null;
  }

  try {
    await store.dispatch('captainAssistantMcpServers/updateEnabledTools', {
      assistantId: assistantId.value,
      serverId: server.id,
      enabledTools: nextEnabled,
    });
  } catch (error) {
    useAlert(error?.message || t('CAPTAIN.ASSISTANT_MCP.SAVE_ERROR'));
  }
};
</script>

<template>
  <PageLayout
    :header-title="t('CAPTAIN.ASSISTANT_MCP.HEADER')"
    :is-fetching="isFetching"
    :is-empty="!allServers.length"
    :show-pagination-footer="false"
    :show-know-more="false"
  >
    <template #emptyState>
      <div class="flex flex-col items-center justify-center gap-4 py-16">
        <div class="i-lucide-plug-zap size-10 text-n-slate-9" />
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANT_MCP.EMPTY.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 text-center max-w-md">
          {{ t('CAPTAIN.ASSISTANT_MCP.EMPTY.SUBTITLE') }}
        </p>
        <router-link
          :to="{ name: 'captain_mcp_servers_index', params: route.params }"
        >
          <Button
            :label="t('CAPTAIN.ASSISTANT_MCP.EMPTY.GO_TO_REGISTRY')"
            size="sm"
          />
        </router-link>
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <p class="text-sm text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANT_MCP.DESCRIPTION') }}
        </p>

        <div
          v-for="server in allServers"
          :key="server.id"
          class="flex flex-col gap-3 p-4 border rounded-lg border-n-weak bg-n-alpha-black1"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="flex flex-col gap-1 min-w-0">
              <h3 class="text-base font-medium text-n-slate-12">
                {{ server.name }}
              </h3>
              <p class="text-xs text-n-slate-11 font-mono truncate">
                {{ server.url }}
              </p>
              <p v-if="!server.enabled" class="text-xs text-n-amber-11">
                {{ t('CAPTAIN.ASSISTANT_MCP.SERVER_DISABLED') }}
              </p>
              <p
                v-else-if="server.discovery_error"
                class="text-xs text-n-ruby-11"
              >
                {{ t('CAPTAIN.ASSISTANT_MCP.SERVER_DISCOVERY_ERROR') }}
              </p>
            </div>
            <Button
              :label="
                isAttached(server.id)
                  ? t('CAPTAIN.ASSISTANT_MCP.DETACH')
                  : t('CAPTAIN.ASSISTANT_MCP.ATTACH')
              "
              :color="isAttached(server.id) ? 'ruby' : 'blue'"
              size="xs"
              :disabled="!server.enabled || !!server.discovery_error"
              @click="handleAttachToggle(server)"
            />
          </div>

          <div
            v-if="
              isAttached(server.id) &&
              server.discovered_tools &&
              server.discovered_tools.length
            "
            class="flex flex-col gap-2 pt-3 border-t border-n-weak"
          >
            <p class="text-xs font-medium text-n-slate-12">
              {{ t('CAPTAIN.ASSISTANT_MCP.ENABLED_TOOLS') }}
            </p>
            <div class="flex flex-col gap-1.5">
              <label
                v-for="tool in server.discovered_tools"
                :key="tool.name"
                class="flex items-start gap-2 text-sm cursor-pointer"
              >
                <input
                  type="checkbox"
                  class="mt-0.5"
                  :checked="isToolEnabled(server, tool.name)"
                  @change="
                    handleToolToggle(
                      server,
                      tool.name,
                      isToolEnabled(server, tool.name)
                    )
                  "
                />
                <span class="flex flex-col gap-0.5 min-w-0">
                  <span class="font-mono text-xs text-n-slate-12">
                    {{ tool.name }}
                  </span>
                  <span v-if="tool.description" class="text-xs text-n-slate-11">
                    {{ tool.description }}
                  </span>
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </template>
  </PageLayout>
</template>
